/**
 * Custom error classes for the configs package.
 * These provide typed, catchable errors for common failure scenarios.
 */

import type { ConfigLevel } from './types.js'

/**
 * Error thrown when a requested entity is not found in the database.
 * This occurs when a fetcher queries for an entity by ID and receives no results.
 *
 * @example
 * ```typescript
 * import { getCustomerConfig, EntityNotFoundError, LEVELS } from '@hopdrive/sdk-configs'
 *
 * try {
 *   const config = await getCustomerConfig({ customerId: 999999 })
 * } catch (err) {
 *   if (err instanceof EntityNotFoundError) {
 *     console.log(`${err.level} ${err.entityId} does not exist`)
 *     if (err.level === LEVELS.CUSTOMER) {
 *       // handle customer not found
 *     }
 *   }
 * }
 * ```
 */
export class EntityNotFoundError extends Error {
  constructor(
    public readonly level: ConfigLevel,
    public readonly entityId: number | bigint
  ) {
    super(`${level} ${entityId} not found`)
    this.name = 'EntityNotFoundError'
  }
}

/**
 * Error thrown when decryption is attempted but the required encryption key
 * version is not available in the configured keys.
 *
 * This occurs when:
 * - An encrypted config value uses a key version that wasn't provided during init()
 * - Encryption keys were not configured at all
 *
 * @example
 * ```typescript
 * import { decryptSecret, EncryptionKeyNotFoundError } from '@hopdrive/sdk-configs'
 *
 * try {
 *   const value = decryptSecret(encryptedValue)
 * } catch (err) {
 *   if (err instanceof EncryptionKeyNotFoundError) {
 *     console.error(`Missing encryption key: ${err.version}`)
 *     console.error(`Available keys: ${err.availableVersions.join(', ')}`)
 *     // Handle missing key - perhaps alert ops to add the key
 *   }
 * }
 * ```
 */
export class EncryptionKeyNotFoundError extends Error {
  constructor(
    /** The key version that was requested but not found (e.g., 'v1', 'v2') */
    public readonly version: string,
    /** List of key versions that are currently available */
    public readonly availableVersions: string[]
  ) {
    const availableMsg =
      availableVersions.length > 0
        ? `Available versions: ${availableVersions.join(', ')}`
        : 'No encryption keys have been configured. Call init() with encryption keys.'

    super(
      `Encryption key version "${version}" not found. ` +
        `An encrypted config value requires this key for decryption. ${availableMsg}`
    )
    this.name = 'EncryptionKeyNotFoundError'
  }
}
